import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Box, Skeleton } from "@ellucian/react-design-system/core";
import SolutionHeader from "./SolutionHeader";
// import { LeaveReviewDialog } from './LeaveReviewDialog';
import SolutionTabs from "./SolutionTabs";
import SolutionSidebar from "./SolutionSidebar";
import { NavigationHeaderBar } from "../../components/Navigation/NavigationHeaderBar";
import { solutionLogoOrDefault, decodeAndCleanText, isSolutionEntitled } from "../../utils";
import { Solution } from "../../types";
import { appConfig } from "../../utils/config";
import { trackSolutionView } from '../../utils/analytics';

const featuresToList = (features: any) => {
  if (!features) return [];
  if (Array.isArray(features)) return features;
  if (typeof features !== "string") return [];
  return features
    .split("\n")
    .map((f) => f.trim())
    .filter((f) => f.length > 0);
};

const parseUseCases = (useCases: any) => {
  if (!useCases) return [];
  if (Array.isArray(useCases)) return useCases;
  if (typeof useCases !== "string") return [];
  return useCases
    .split("\n")
    .map((uc) => uc.trim())
    .filter((uc) => uc.length > 0);
};

const normalizeManifestName = (manifestName: unknown): string | null => {
  if (typeof manifestName !== "string") {
    return null;
  }

  const trimmedManifestName = manifestName.trim();
  return trimmedManifestName.length > 0 ? trimmedManifestName : null;
};

const SolutionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [solution, setSolution] = useState<Solution | null>(null);

  const isLoggedIn = localStorage.getItem("loggedIn") === "true";

  // Could be affected by push-state id changes too
  /**
   * Effect: Gets the full list of solutions that we then locally find the one to show.
   */
  useEffect(() => {
    // TODO: Use loading animation or something so we don't see "no results" while this comes back.
    // Simulate an API call to fetch solutions
    const fetchSolutions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Replace with your API call
        const apiToCall = `${appConfig.api.baseUrl}/api/solutions/${id}`;
        const response = await fetch(apiToCall);
        const data = await response.json();

        // TODO: If there isn't a "solutions" item, then we have a problem
        // for each solution we'll make sure there are some data
        if (!data) {
          setSolution(null);
          return;
        }

        // Clean up text fields that may contain HTML entities or weird characters
        if (data.description) {
          data.description = decodeAndCleanText(data.description);
        }
        if (data.summary) {
          data.summary = decodeAndCleanText(data.summary);
        }
        if (data.name) {
          data.name = decodeAndCleanText(data.name);
        }
        // Features and useCases are strings that get parsed into arrays later,
        // so clean them before parsing
        if (data.features && typeof data.features === "string") {
          data.features = decodeAndCleanText(data.features);
        }
        if (data.useCases && typeof data.useCases === "string") {
          data.useCases = decodeAndCleanText(data.useCases);
        }

        data.manifestName = normalizeManifestName(data.manifestName);

        // Set isEntitled using full solution so name/fullHierarchy can match
        data.isEntitled = isSolutionEntitled(data, isLoggedIn);

        setSolution(data || null);

        // Track solution view when solution is successfully loaded
        if (data && data.name) {
          trackSolutionView(data);
        } else {
          console.warn('No solution data to track');
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load solutions");
        setSolution(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSolutions();
  }, [id, isLoggedIn]);

  // Responsive state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [isTablet, setIsTablet] = useState(
    window.innerWidth >= 768 && window.innerWidth <= 1023
  );

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 767);
      setIsTablet(width >= 768 && width <= 1023);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Scroll to top on mount (when navigating to detail page)
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location]);

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installComplete, setInstallComplete] = useState(false);

  // const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  return (
    <>
      <div
        className="app-shell"
        style={{ background: "#FFF", minHeight: "100vh" }}
      >
        {/* Sticky Header with NavigationHeaderBar only */}
        <div
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            background: "var(--Primary-iris-600, #7100EB)", // Purple background
            transition: "background 0.3s ease"
          }}
        >
          <NavigationHeaderBar
            isAtTop={false} // Always visible on details page
            snackbarOpen={snackbarOpen}
            onSnackbarClose={() => setSnackbarOpen(false)}
            snackbarMessage="Installation completed successfully"
          />
        </div>

        {loading && (
          // Could be a property of SolutionHeader so we don't have copy/paste here
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              padding: isMobile ? "16px" : "24px 32px 32px 32px",
              background: "linear-gradient(90deg, #FFF 0%, #EAFDEC 100%)",
              boxSizing: "border-box",
              zIndex: 1
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: isMobile ? "16px" : isTablet ? "24px" : "32px",
                alignSelf: "stretch",
                flexDirection: isMobile ? "column" : "row",
                ...(isMobile && { alignItems: "center" })
              }}
            >
              {/* Logo */}
              <Box
                sx={{
                  width: isMobile ? 86 : isTablet ? 128 : 160,
                  height: isMobile ? 86 : isTablet ? 128 : 160,
                  aspectRatio: "1/1",
                  borderRadius: "6px",
                  background: "#E9E9E9",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden"
                }}
              >
                <Skeleton
                  rectangle={{ width: "100%", height: "100%", borderRadius: 6 }}
                />
              </Box>
              {/* Right Content */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  minWidth: 0
                }}
              >
                {/* Title + Install Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center",
                    alignSelf: "stretch",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? "16px" : "0",
                    paddingBottom: "32px"
                  }}
                >
                  <Skeleton
                    rectangle={{
                      width: "60%",
                      height: isMobile ? "28px" : isTablet ? "36px" : "48px",
                      ...(isMobile && { textAlign: "center" })
                    }}
                  />
                </Box>
                <Skeleton rectangle={{ width: "100%", height: "164px" }} />
              </Box>
            </Box>
          </Box>
        )}
        {!loading && solution && (
          <>
            <SolutionHeader
              logo={solutionLogoOrDefault(solution.solutionImage, solution.category)}
              title={solution.name}
              rating={solution.ratingLabel ?? "Not rated"}
              installs={solution.installs}
              headline={solution.headline}
              description={solution.description}
              // snackbarOpen and setSnackbarOpen removed
              installing={installing}
              setInstalling={setInstalling}
              installComplete={installComplete}
              setInstallComplete={setInstallComplete}
              isEntitled={solution.isEntitled ?? false}
              isLoggedIn={isLoggedIn}
              isMobile={isMobile}
              isTablet={isTablet}
              solution={solution}
            />
            {/* ReviewConfirmationDialog and LeaveReviewDialog removed */}
            <Box
              sx={{
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                width: "100%",
                minHeight: "calc(100vh - 80px)",
                bgcolor: "#fff",
                ...(isMobile && { padding: "0 16px" }),
                ...(!isMobile && !isTablet && { padding: "0 80px" }),
                ...(isTablet && { padding: "0 32px" })
              }}
            >
              {/* Left gutter - removed, using container padding instead */}

              {/* Main content area */}
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  marginRight: isMobile ? 0 : isTablet ? "32px" : "64px",
                  ...(isMobile && { paddingBottom: 2 })
                }}
              >
                <SolutionTabs
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  description={solution.description}
                  image={solutionLogoOrDefault(solution.solutionImage, solution.category)}
                  solutionVideo={solution.solutionVideo}
                  reviews={[]}
                  features={featuresToList(solution.features)}
                  useCases={parseUseCases(solution.useCases)}
                  dependencies={solution.dependencies ?? []}
                  supportedRegions={solution.supportedRegions ?? []}
                  supportedLanguages={solution.supportedLanguages ?? []}
                  integratedEllucianSolutions={
                    solution.integratedEllucianSolutions
                  }
                  isMobile={isMobile}
                  isTablet={isTablet}
                />
              </Box>

              {/* Right sidebar gutter - removed */}

              {/* Sidebar */}
              <Box
                sx={{
                  width: isMobile ? "100%" : isTablet ? "300px" : "320px",
                  flexShrink: 0,
                  bgcolor: "#fff",
                  ...(isMobile && {
                    marginTop: 3,
                    borderTop: "1px solid #E0E0E0",
                    paddingTop: 3
                  })
                }}
              >
                <SolutionSidebar
                  createdBy={
                    solution.builtBy || solution.accountName || "Unknown"
                  }
                  tags={solution.tags}
                  type={(() => {
                    const raw = Array.isArray(solution.type)
                      ? (solution.type.length > 0 ? solution.type.join(", ") : undefined)
                      : (solution.type || undefined);
                    if (!raw) return undefined;
                    return raw.toLowerCase().includes("experience") ? "Experience Card" : raw;
                  })()}
                  license={"Subscription"}
                  documentation={
                    [
                      ...(solution.documentationUrl ? [{
                        label: "Documentation",
                        url: solution.documentationUrl
                      }] : []),
                      ...(solution.supportUrl ? [{
                        label: "Support",
                        url: solution.supportUrl
                      }] : []),
                      ...(solution.salesUrl ? [{
                        label: "Sales",
                        url: solution.salesUrl
                      }] : [])
                    ]
                  }
                  isMobile={isMobile}
                  isTablet={isTablet}
                />
              </Box>

              {/* Right gutter - removed, using container padding instead */}
            </Box>
          </>
        )}
        {!loading && !solution && (
          <Box sx={{ padding: 4, textAlign: "center" }}>
            <h2>Solution Not Found</h2>
            <p>The solution you are looking for does not exist.</p>
          </Box>
        )}
      </div>
    </>
  );
};

export default SolutionDetails;
